import { NgClass } from '@angular/common';
import { Component, ElementRef, input, InputSignal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SkeletonLoaderComponent } from "@shared/components/ui/skeleton-loader";
import { TooltipDirective, TooltipFollowDirective, TruncateTooltipDirective } from '@shared/directives';

@Component({
  selector: 'lib-input-wrapper',
  templateUrl: './input-wrapper.component.html',
  styleUrl: './input-wrapper.component.css',
  imports: [
    SkeletonLoaderComponent,
    TranslateModule,
    NgClass,
    TooltipDirective,
    TooltipFollowDirective,
    TruncateTooltipDirective
  ],
})
export class InputWrapperComponent {

  /**
   * The id of the input element.
   */
  public readonly for: InputSignal<string> = input<string>('');

  /**
   * The label of the input element.
   * @default ''
   */
  public readonly label: InputSignal<string> = input<string>('');

    /**
   * The label of the input element.
   * @default null
   */
    public readonly contentRight: InputSignal<string | number> = input<string | number>(null);

  /**
   * i18n key of a hover explanation for the field. Given one, the wrapper draws the
   * standard `fi-rr-info` beside the label and hangs the tooltip on it — the shape
   * `lib-enrollment-number` had inline, moved here so every field spells it the same way.
   *
   * Pass the KEY, not the translated text: `lib-tooltip` translates it internally.
   *
   * For an explanation that has to stay on screen, or that carries its own markup, this
   * is the wrong tool — use `[libTooltipStatic]` / `[libTooltipFollow]` on your own
   * element. See the tooltip table in CLAUDE.md.
   * @default ''
   */
  public readonly infoTooltip: InputSignal<string> = input<string>('');

  /**
   * The same icon, for an explanation that a line of text cannot carry — a list, a table
   * of tokens, badges. Hand the wrapper your balloon element (`#helpTooltip` in the
   * parent) and it hangs it on the icon with `libTooltipFollow`.
   *
   * The markup stays with the parent, where its bindings live, and the positioning stays
   * in the directive. **An output on hover would be the wrong shape**: whoever received it
   * would have to open, place, clamp to the viewport, flip near the edges and clean up the
   * balloon by hand — the four things CLAUDE.md says not to reimplement.
   *
   * `Follow` and not `Static` because that is what `lib-enrollment-number` had, and a
   * balloon that stops chasing the cursor reads as a different control to whoever was
   * using it. Swapping the two here changes every consumer at once.
   *
   * Takes precedence over {@link infoTooltip}: passing both would stack two balloons on
   * one icon, so the plain-text one steps aside.
   * @default null
   */
  public readonly infoTooltipRef: InputSignal<ElementRef<HTMLElement> | HTMLElement> =
    input<ElementRef<HTMLElement> | HTMLElement>(null);

  /**
   * The flag to indicate if the input is required.
   * @default false.
   */
  public readonly required: InputSignal<boolean> = input<boolean>(false);

  public readonly skeleton: InputSignal<boolean> = input<boolean>(false);

  public readonly disabled: InputSignal<boolean> = input<boolean>(false);

  public readonly truncateLabel: InputSignal<boolean> = input<boolean>(true);

  public readonly flexDirection: InputSignal<'row' | 'column'> = input<'row' | 'column'>('column');
  public readonly heightFull: InputSignal<boolean> = input<boolean>(false);
}
